import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  createIntakeDraft,
  discardIntakeDraft,
  listAuditors,
  listSectors,
  listOshmsSectors,
  listStandards,
  readIntakeDraft,
  reviseIntake,
  saveIntakeDraft,
  submitIntake,
  validateIntake,
  type AllocationGate,
  type AuditorSummary,
  type IntakeDraft,
  type IntakeIssue,
  type IntakeValidation,
  type OshmsSector,
  type Sector,
} from "../api/client";
import { Acknowledgement } from "../components/Acknowledgement";
import { BooleanChoice } from "../components/BooleanChoice";
import { Callout } from "../components/Callout";
import { DateField } from "../components/DateField";
import { MultiSelect } from "../components/MultiSelect";
import { SelectField } from "../components/SelectField";
import { TextAreaField } from "../components/TextAreaField";
import { TextField } from "../components/TextField";
import { idempotencyKey } from "../api/idempotency";

/**
 * Screens 24 to 29 — the intake, and the gates on who may be allocated to it.
 *
 * Six numbered cards, ported from the prototype. Three things on it are **not** ported,
 * and each is a place the prototype relaxes on purpose so a reviewer can walk the flow
 * without filling forms (data dictionary §8):
 *
 * * the certification scope statement, the products and the planned date carry no asterisk
 *   there and are all `NOT NULL` here;
 * * the contractor field appears **only** when ISO 45001 is in scope, because Cl. 1.9
 *   counts contractors toward effective personnel for that standard alone;
 * * "Process complexity" is a two-value select reading `Standard / Low complexity`, which
 *   is neither of the taxonomies MD 5 uses. It is replaced by the Table OH&SMS 2 sector
 *   and the category it defaults to.
 *
 * **Every regulatory answer on this screen is the server's.** Whether contractors apply,
 * what the effective personnel figure is, what the sector defaults to, whether the
 * allocated auditor may be allocated: all of it arrives from `POST /audits/validation`,
 * because each is a clause and a second copy of a clause in the browser is a copy that can
 * disagree with the engine.
 *
 * Numeric and email inputs are `type="text"` with an `inputMode`. `type="number"` and
 * `type="email"` return null from `selectionStart`, so a re-rendering form cannot restore
 * the caret and text appears to type backwards — a real defect, not a hypothetical.
 */
export function IntakeForm({
  draftId,
  revision,
  onCancel,
  onCreated,
}: {
  /** Resuming a part-typed intake — screen 49. Absent for a new one. */
  draftId?: string;
  /**
   * Screen 48 — the same six cards over an audit that already exists.
   *
   * One form rather than two, because a revision is refused unless it is *complete*: the
   * server re-validates the whole payload and re-runs the determination from it, so a
   * cut-down "edit the figures" screen would be a second, laxer copy of the intake rules.
   * What differs is where the values come from and what submitting costs — and the cost is
   * the dialog, not a narrower form.
   */
  revision?: Revision;
  onCancel: () => void;
  onCreated: (auditId: string) => void;
}) {
  const [draft, setDraft] = useState<IntakeDraft>(
    revision ? { ...EMPTY, ...revision.intake } : EMPTY,
  );
  const [validation, setValidation] = useState<IntakeValidation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(draftId === undefined);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  /**
   * Whether the body has asked to move on yet — the same gate the two onboarding wizards
   * use (`attempted` in `CertificationBodyOnboarding` and `AuditorOnboarding`).
   *
   * The server validates from the first render, because every answer on this screen is a
   * clause and the browser holds no second copy of one. What it must not do is *shout*
   * from the first render: an empty intake is incomplete by definition, so rendering the
   * blocking findings immediately painted every required field red before anything had
   * been typed. Nothing was wrong — nothing had been answered. Advisory findings are shown
   * throughout, because those say something the body did not already know.
   */
  const [attempted, setAttempted] = useState(false);

  const [sectors, setSectors] = useState<Sector[]>([]);
  const [oshms, setOshms] = useState<OshmsSector[]>([]);
  const [standards, setStandards] = useState<string[]>([]);
  const [auditors, setAuditors] = useState<AuditorSummary[]>([]);

  const held = useRef<string | undefined>(draftId);

  useEffect(() => {
    Promise.all([listSectors(), listOshmsSectors(), listStandards(), listAuditors()])
      .then(([s, o, st, a]) => {
        setSectors(s);
        setOshms(o);
        setStandards(st);
        setAuditors(a);
      })
      .catch((cause: unknown) => setError(message(cause, "The reference lists")));
  }, []);

  useEffect(() => {
    if (draftId === undefined) return;
    readIntakeDraft(draftId)
      .then((saved) => {
        setDraft({ ...EMPTY, ...saved.draft });
        setLoaded(true);
      })
      .catch((cause: unknown) => setError(message(cause, "The draft")));
  }, [draftId]);

  // The server answers on every change. Debounced, because it is a round trip per
  // keystroke otherwise — and not skipped, because everything it answers is a clause.
  useEffect(() => {
    if (!loaded) return;
    const timer = window.setTimeout(() => {
      validateIntake(draft)
        .then(setValidation)
        .catch(() => undefined);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [draft, loaded]);

  const set = useCallback(
    <K extends keyof IntakeDraft>(key: K, value: IntakeDraft[K]) =>
      setDraft((current) => ({ ...current, [key]: value })),
    [],
  );

  // Six cards is more than one screen, so the first thing that needs an answer is brought
  // into view rather than left for the body to hunt for. Only when they ask to proceed.
  const board = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!attempted) return;
    board.current
      ?.querySelector('[aria-invalid="true"], .msd.invalid, .err')
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [attempted]);

  const advisory = useMemo(() => index(validation, "advisory"), [validation]);
  const blocking = useMemo(() => index(validation, "blocking"), [validation]);
  const issues = useMemo(
    // Blocking wins where both name the same field, as it did when this was one map.
    () => (attempted ? { ...advisory, ...blocking } : advisory),
    [attempted, blocking, advisory],
  );
  const outstanding = useMemo(() => Object.keys(blocking).length, [blocking]);
  const complete = validation?.complete === true;
  const contractorsApply = validation?.contractors_apply === true;
  const allocation = validation?.allocation ?? null;

  const saveDraft = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = held.current
        ? await saveIntakeDraft(held.current, draft)
        : await createIntakeDraft(draft);
      held.current = saved.id;
      setSavedAt(saved.updated_at);
    } catch (cause) {
      setError(message(cause, "The draft"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Reveal what is outstanding, and go no further until nothing is.
   *
   * The button is live rather than disabled, because a disabled button on a form that has
   * not said what is wrong with it leaves the body with nothing to act on. Pressing it is
   * how they ask, and it is what turns the blocking findings on. An incomplete intake
   * still cannot be submitted — the guard is here and again at the server.
   */
  const askedToProceed = (): boolean => {
    setAttempted(true);
    return complete;
  };

  const run = async () => {
    if (!askedToProceed()) return;
    setBusy(true);
    setError(null);
    try {
      const audit = await submitIntake(
        { ...draft, draft_id: held.current },
        idempotencyKey(),
      );
      onCreated(audit.id);
    } catch (cause) {
      setError(message(cause, "The intake"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Assertion 8, from the certification body's side.
   *
   * The dialog is confirmed before this runs, because the cascade is not recoverable by
   * undoing the edit: the approvals are withdrawn and re-approving is two people's work,
   * and the plan drops to draft. The dialog states that; this performs it.
   */
  const reviseAudit = async () => {
    if (!revision) return;
    setBusy(true);
    setError(null);
    try {
      await reviseIntake(revision.auditId, draft, idempotencyKey());
      setConfirming(false);
      revision.onRevised();
    } catch (cause) {
      setError(message(cause, "The revision"));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (held.current) await discardIntakeDraft(held.current).catch(() => undefined);
    onCancel();
  };

  if (!loaded) return <p className="sub">Reading the draft&hellip;</p>;

  return (
    <div ref={board}>
      {revision ? null : (
        <button type="button" className="backlink linkish" onClick={onCancel}>
          ← All audits
        </button>
      )}

      <div className="between" style={{ marginBottom: "18px" }}>
        <div>
          <div className="eyebrow">Step 1 · Certification body</div>
          <h1>{revision ? "Intake — revision" : "New audit — intake"}</h1>
          <p className="sub">
            Fields marked <b style={{ color: "var(--colour-semantic-signal-red)" }}>*</b> are
            required. Audit type governs which conditional fields appear and how the
            duration is calculated.
          </p>
        </div>
      </div>

      {/*
        Said before the first field is touched rather than in the dialog alone. A body that
        reaches this screen to correct a typo and discovers on submission that it has
        withdrawn two approvals has been told too late.
      */}
      {revision ? (
        <Callout tone="warn" title="Revising this intake re-runs Steps 2 to 5.">
          The determination is recalculated and persisted as a new version, both Step 2
          approvals are withdrawn, and an approved plan drops to draft with its edits
          retained. The audit moves to re-confirmation — specification §5.1 step 6. Nothing
          out of date reaches audit day, which is why the cost is charged rather than the
          change refused. Currently at intake version{" "}
          <span className="mono">{revision.version}</span>.
        </Callout>
      ) : null}

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {/* ---- 1 · Organisation details ---------------------------------------------- */}
      <div className="card">
        <h2>1 · Organisation details</h2>
        <div className="grid2">
          <TextField
            id="client-organisation"
            label="Organisation name"
            value={draft.client_organisation ?? ""}
            onChange={(value) => set("client_organisation", value)}
            placeholder="Legal entity name"
            error={issues.client_organisation}
            required
          />
          <div className="field">
            <label className="f" htmlFor="sector-indexes">
              Industry sector <b>*</b>{" "}
              <i>IAF technical sector · select one or more</i>
            </label>
            <MultiSelect
              id="sector-indexes"
              options={sectors.map((sector) => ({
                value: sector.index,
                label: sector.label,
              }))}
              selected={draft.sector_indexes ?? []}
              onToggle={(value) =>
                set(
                  "sector_indexes",
                  toggle(draft.sector_indexes ?? [], value),
                )
              }
              onSetAll={(values) =>
                set("sector_indexes", [...values].sort((a, b) => a - b))
              }
              placeholder="— Select sectors —"
              invalid={Boolean(issues.sector_indexes)}
              searchLabel="Filter sectors"
            />
            {issues.sector_indexes ? (
              <p className="err" role="alert">
                {issues.sector_indexes}
              </p>
            ) : null}
          </div>
        </div>

        <TextAreaField
          id="client-address"
          label="Physical address / site(s)"
          value={draft.client_address ?? ""}
          onChange={(value) => set("client_address", value)}
          placeholder="Address of site(s) to be audited"
          error={issues.client_address}
        />

        <div className="grid2">
          <TextField
            id="contact-name"
            label="Primary contact name"
            value={draft.contact_name ?? ""}
            onChange={(value) => set("contact_name", value)}
            placeholder="Full name"
            error={issues.contact_name}
            required
          />
          <TextField
            id="contact-email"
            label="Primary contact email"
            kind="email"
            hint="receives the access link"
            value={draft.contact_email ?? ""}
            onChange={(value) => set("contact_email", value)}
            placeholder="name@organisation.example"
            error={issues.contact_email}
            required
          />
        </div>
      </div>

      {/* ---- 2 · Management system scope ------------------------------------------- */}
      <div className="card">
        <h2>2 · Management system scope</h2>
        {/*
          Required, against a prototype that leaves both unmarked. `certification_scope`
          and `products_services` are `NOT NULL` and data dictionary §8 names them: the
          scope statement is what the certificate will say, and an audit conducted against
          an unstated scope has nothing to report against.
        */}
        <TextAreaField
          id="certification-scope"
          label="Certification scope statement"
          value={draft.certification_scope ?? ""}
          onChange={(value) => set("certification_scope", value)}
          placeholder="The proposed scope of certification"
          error={issues.certification_scope}
          required
        />
        <div className="grid2">
          <TextField
            id="products-services"
            label="Products and services covered"
            value={draft.products_services ?? ""}
            onChange={(value) => set("products_services", value)}
            error={issues.products_services}
            required
          />
          <TextField
            id="exclusions"
            label="Exclusions"
            hint="optional"
            value={draft.exclusions ?? ""}
            onChange={(value) => set("exclusions", value)}
            placeholder="e.g. Cl. 8.3 Design and development"
          />
        </div>
      </div>

      {/* ---- 3 · Audit details ------------------------------------------------------ */}
      <div className="card">
        <h2>3 · Audit details</h2>
        <div className="field">
          <label className="f">
            Standard(s) <b>*</b> <i>multi-select for an integrated system</i>
          </label>
          <div className="chips">
            {standards.map((standard) => {
              const on = (draft.standards ?? []).includes(standard);
              return (
                <button
                  key={standard}
                  type="button"
                  className="chip"
                  aria-pressed={on}
                  onClick={() =>
                    set(
                      "standards",
                      on
                        ? (draft.standards ?? []).filter((s) => s !== standard)
                        : [...(draft.standards ?? []), standard],
                    )
                  }
                >
                  {standard}
                </button>
              );
            })}
          </div>
          <div className="sub" style={{ fontSize: "11.5px", marginTop: "7px" }}>
            Limited to this certification body&rsquo;s accredited scope — per standard{" "}
            <i>and</i> per technical sector.
          </div>
          {issues.standards ? (
            <p className="err" role="alert">
              {issues.standards}
            </p>
          ) : null}
        </div>

        <div className="grid3">
          <SelectField
            id="audit-type"
            label="Audit type"
            value="stage_1"
            onChange={() => undefined}
            options={[{ value: "stage_1", label: "Stage 1 — initial certification" }]}
            placeholder="Stage 1 — initial certification"
            hint="Stage 2, surveillance and recertification are out of MVP scope"
            disabled
            required
          />
          <TextField
            id="delivery-method"
            label="Delivery method"
            value="Remote — ICT"
            onChange={() => undefined}
            hint="every audit is ICT-conducted, so MD 4 gates all of them"
          />
          <DateField
            id="planned-date"
            label="Planned audit date"
            value={draft.planned_date ?? ""}
            onChange={(value) => set("planned_date", value)}
            error={issues.planned_date}
            required
          />
        </div>
      </div>

      {/* ---- 4 · Organisation size --------------------------------------------------- */}
      <div className="card">
        <h2>4 · Organisation size</h2>
        {/*
          The prototype says these "cannot be edited after Step 2 approval", and that is one
          of the places screen 24 relaxes into being wrong rather than merely permissive:
          assertion 8 revises exactly these figures on an audit whose approvals are both in.
          They can be edited, up until the session opens, and what stops it being a quiet
          change is that the edit withdraws the approvals and says so.
        */}
        <p className="sub" style={{ marginBottom: "14px" }}>
          These figures produce the effective number of personnel used for the audit
          duration calculation. Changing them after Step 2 approval re-runs the
          determination and withdraws both approvals.
        </p>

        <div className="grid3">
          <TextField
            id="permanent-staff"
            label="Permanent employees"
            kind="numeric"
            hint="full-time equivalents"
            value={draft.permanent_staff ?? ""}
            onChange={(value) => set("permanent_staff", value)}
            placeholder="0"
            error={issues.permanent_staff}
            required
          />
          <TextField
            id="temporary-staff"
            label="Temporary / contract"
            kind="numeric"
            value={draft.temporary_staff ?? ""}
            onChange={(value) => set("temporary_staff", value)}
            placeholder="0"
            error={issues.temporary_staff}
          />
          <TextField
            id="part-time-staff"
            label="Part-time"
            kind="numeric"
            hint="weighted at 0.5"
            value={draft.part_time_staff ?? ""}
            onChange={(value) => set("part_time_staff", value)}
            placeholder="0"
            error={issues.part_time_staff}
          />
        </div>

        {/*
          Cl. 1.9 — the field exists only where an OH&SMS standard is in scope, and whether
          it does is the server's answer. A count typed here and then not counted would
          disappear from a determination without saying so.
        */}
        {contractorsApply ? (
          <div className="grid3">
            <TextField
              id="contractor-staff"
              label="Contractors"
              kind="numeric"
              hint="under the organisation's control or influence — IAF MD 5 Cl. 1.9"
              value={draft.contractor_staff ?? ""}
              onChange={(value) => set("contractor_staff", value)}
              placeholder="0"
              error={issues.contractor_staff}
            />
          </div>
        ) : blocking.contractor_staff ? (
          /* Read from `blocking` rather than `issues`, so it is not held back until the
             body asks to proceed. It cannot fire on an untouched intake — it says a
             number that *was* typed will not be counted, and the field it was typed into
             has just left the screen. That is exactly when it needs saying. */
          <Callout tone="block" title="Contractors do not apply to this audit.">
            {blocking.contractor_staff}
          </Callout>
        ) : null}

        <div className="grid3">
          <TextField
            id="site-count"
            label="Number of sites"
            kind="numeric"
            value={draft.site_count ?? ""}
            onChange={(value) => set("site_count", value)}
            error={issues.site_count}
            required
          />
          <BooleanChoice
            id="shift-work"
            label="Shift work"
            value={draft.shift_work ?? null}
            onChange={(value) => set("shift_work", value)}
            error={issues.shift_work}
            negativeFirst
            required
          />
          <div />
        </div>

        {/*
          The prototype's "Process complexity · Standard / Low complexity" is neither MD 5
          taxonomy. Replaced by the two answers the engine actually needs: the Table OH&SMS 2
          sector, and the category — which defaults to the sector's and may only be lowered.
        */}
        <div className="grid2">
          <SelectField
            id="oshms-sector"
            label="OH&S risk sector"
            hint="IAF MD 5 Table OH&SMS 2 · required only where ISO 45001 or ISO 14001 is in scope"
            value={draft.oshms_sector ?? ""}
            onChange={(value) => {
              set("oshms_sector", value);
              set("complexity", null);
              set("complexity_justification", null);
            }}
            options={oshms.map((sector) => ({
              value: sector.description,
              label: `${sector.description} — ${sector.default_complexity}`,
            }))}
            placeholder="Not applicable"
            error={issues.oshms_sector}
          />
          <SelectField
            id="complexity"
            label="Process complexity"
            hint="the sector default, or lower with a justification"
            value={draft.complexity ?? ""}
            onChange={(value) => set("complexity", value || null)}
            options={lowerings(validation?.sector_default_complexity ?? null)}
            placeholder={
              validation?.sector_default_complexity
                ? `${validation.sector_default_complexity} — sector default`
                : "— Choose the OH&S risk sector first —"
            }
            error={issues.complexity}
            disabled={!validation?.sector_default_complexity}
          />
        </div>

        {draft.complexity && draft.complexity !== validation?.sector_default_complexity ? (
          <TextAreaField
            id="complexity-justification"
            label="Justification for the lower category"
            hint="visible to an accreditation body assessor · at least 10 characters"
            value={draft.complexity_justification ?? ""}
            onChange={(value) => set("complexity_justification", value)}
            placeholder="Why is this organisation below its sector default?"
            error={issues.complexity_justification}
            required
          />
        ) : null}

        <Callout
          tone="info"
          title={`Effective personnel: ${validation?.effective_personnel ?? "—"}`}
        >
          <span className="mono">
            {validation?.personnel_basis ??
              "Choose the standards and enter the permanent headcount."}
          </span>
          <br />
          <span className="sub">
            Duration is calculated at Step 2 and cannot be entered manually.
          </span>
        </Callout>
      </div>

      {/* ---- 5 · Audit team — the gates, M2-3 ---------------------------------------- */}
      <div className="card">
        <h2>5 · Audit team</h2>

        {allocation
          ? allocation.gates
              .filter((gate) => gate.blocks)
              .map((gate) => (
                <div className="warnbar" key={gate.gate}>
                  <b>Allocation blocked — {gate.clause}.</b> {gate.detail}
                </div>
              ))
          : null}

        <div className="grid2">
          <div>
            <SelectField
              id="lead-auditor"
              label="Allocated lead auditor"
              hint="from your auditor register"
              value={draft.lead_auditor_id ?? ""}
              onChange={(value) => set("lead_auditor_id", value)}
              options={auditors.map((auditor) => ({
                value: auditor.id,
                label: auditorLabel(auditor),
              }))}
              error={allocation ? undefined : issues.lead_auditor_id}
              required
            />
            {/*
              A warn is not a refusal and must not look like one. The certification body
              owns the competence decision (Cl. 7.1.2) — what the platform owes is that the
              departure is recorded, and it is: submitting writes it to the audit's chain.
            */}
            {allocation?.gates
              .filter((gate) => !gate.blocks)
              .map((gate) => (
                <p className="err" key={gate.gate}>
                  {gate.detail}
                </p>
              ))}
          </div>

          <div>
            <BooleanChoice
              id="technical-expert-required"
              label="Technical expert required"
              value={draft.technical_expert_required ?? null}
              onChange={(value) => set("technical_expert_required", value)}
              error={issues.technical_expert_required}
              negativeFirst
              required
            />
            {draft.technical_expert_required ? (
              <TextField
                id="technical-expert"
                label="Technical expert"
                hint="excluded from table time — IAF MD 5 Cl. 3.8"
                value={draft.technical_expert ?? ""}
                onChange={(value) => set("technical_expert", value)}
                error={issues.technical_expert}
                required
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* ---- 6 · Impartiality and consent -------------------------------------------- */}
      <div className="card">
        <h2>6 · Impartiality and consent</h2>
        <BooleanChoice
          id="consultancy-declared"
          label="Management system consultancy provided to this client?"
          hint="ISO/IEC 17021-1 Cl. 4.2"
          value={draft.consultancy_declared ?? null}
          onChange={(value) => set("consultancy_declared", value)}
          error={issues.consultancy_declared}
          negativeFirst
          required
        />
        {draft.consultancy_declared ? (
          <Callout tone="warn">
            Cl. 4.2 restricts certification where the body has provided management system
            consultancy. Complao records the declaration and flags the audit for
            impartiality review. It does not block.
          </Callout>
        ) : null}

        <div className="hr" />

        <Acknowledgement
          id="ict-consent"
          title="ICT consent — IAF MD 4:2025."
          checked={draft.ict_consent_cb ?? false}
          error={issues.ict_consent_cb}
          onChange={(value) => set("ict_consent_cb", value)}
        >
          The client has been informed of, and consents to, the use of information and
          communication technology for the conduct of this remote Stage 1 audit.
          <span
            style={{
              display: "block",
              color: "var(--colour-brand-amber-deep)",
              marginTop: "4px",
            }}
          >
            The auditee re-confirms this in their own name at session start; this box records
            that the certification body has obtained consent.
          </span>
        </Acknowledgement>

        <Acknowledgement
          id="confidentiality-ack"
          title="Confidentiality notice — ISO/IEC 17021-1 Cl. 4.6."
          checked={draft.confidentiality_ack ?? false}
          error={issues.confidentiality_ack}
          onChange={(value) => set("confidentiality_ack", value)}
        >
          All information shared during this audit is confidential and will be used solely
          for the purpose of this certification audit.
        </Acknowledgement>
      </div>

      {/*
        No "save draft" on a revision. A draft is a part-typed intake that has not become an
        audit; this one already is one, and a half-saved revision would leave the audit
        describing neither what was determined nor what is being proposed.
      */}
      <div className="row" style={{ justifyContent: "flex-end", gap: "10px" }}>
        {revision ? (
          <>
            <button type="button" className="btn sec" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                if (askedToProceed()) setConfirming(true);
              }}
              disabled={busy}
            >
              {busy ? "Working…" : "Revise and re-run"}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn sec" onClick={discard} disabled={busy}>
              Discard
            </button>
            <button type="button" className="btn sec" onClick={saveDraft} disabled={busy}>
              Save draft and exit
            </button>
            <button type="button" className="btn" onClick={run} disabled={busy}>
              {busy ? "Working…" : "Run scope determination"}
            </button>
          </>
        )}
      </div>

      <p className="sub" style={{ textAlign: "right", marginTop: "8px" }}>
        {savedAt ? `Draft saved ${savedAt.slice(0, 16).replace("T", " ")}. ` : ""}
        {footnote(complete, attempted, outstanding)}
      </p>

      {confirming && revision ? (
        <ReconfirmationDialog
          version={revision.version}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void reviseAudit()}
        />
      ) : null}
    </div>
  );
}

/**
 * What is left, in the words the onboarding wizard uses for the same state.
 *
 * The count is only offered once the findings themselves are on screen. "Eight
 * outstanding" beside a form showing none of them tells the body they are behind without
 * telling them on what.
 */
function footnote(complete: boolean, attempted: boolean, outstanding: number): string {
  if (complete) return "Every card is complete.";
  if (attempted && outstanding > 0) {
    return `Complete the required fields to continue — ${outstanding} outstanding.`;
  }
  return "Complete all required fields and both acknowledgements to continue.";
}

/** What the certification body is about to spend, named before it spends it. */
interface Revision {
  auditId: string;
  /** The `intake_submissions` version being replaced. The next one is written on success. */
  version: number;
  intake: IntakeDraft;
  onRevised: () => void;
}

/**
 * Screen 48's cost, stated as a confirmation.
 *
 * It lists what the server will do rather than asking "are you sure?" — the four
 * consequences are the ones assertion 8 names, and each is work for somebody: two people
 * must approve again, and the lead auditor's plan returns to draft. A dialog that does not
 * say what it costs is a dialog people click through.
 */
function ReconfirmationDialog({
  version,
  busy,
  onCancel,
  onConfirm,
}: {
  version: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onCancel]);

  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <h2>Revise the intake and re-run Steps 2 to 5</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          Intake version <span className="mono">{version}</span> is retained and version{" "}
          <span className="mono">{version + 1}</span> is written. Nothing is deleted — the
          audit file explains both figures.
        </p>
        <ul className="trail" style={{ marginBottom: "14px" }}>
          <li>
            <b>The determination is recalculated</b>
            <span className="m">
              Persisted as a new calculation under the ruleset in force now. The earlier one
              stays with the factors that made it.
            </span>
          </li>
          <li className="ov">
            <b>Both Step 2 approvals are withdrawn</b>
            <span className="m">
              Each was recorded against figures that no longer describe this audit. Both
              parties must approve again.
            </span>
          </li>
          <li className="ov">
            <b>An approved plan returns to draft</b>
            <span className="m">
              The superseded version is retained with the lead auditor&rsquo;s amendments
              attached to the figures they were made against.
            </span>
          </li>
          <li>
            <b>The audit moves to re-confirmation</b>
            <span className="m">
              Recorded on the append-only trail with what moved and why.
            </span>
          </li>
        </ul>
        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : "Revise and re-run"}
          </button>
        </div>
      </div>
    </div>
  );
}

const EMPTY: IntakeDraft = {
  sector_indexes: [],
  standards: [],
  audit_type: "stage_1",
  site_count: "1",
};

/**
 * The categories a certification body may choose, given the sector's default.
 *
 * The default and everything below it. There is no raising path — a category above the
 * sector default is a documented risk judgement recorded through the same route, and
 * `complexity_source` offers `sector_default` and `lowered_by_cb` and no third value. The
 * server refuses a raise; the select does not offer one.
 */
function lowerings(sectorDefault: string | null) {
  const order = ["high", "medium", "low", "limited"];
  if (!sectorDefault) return [];
  const from = order.indexOf(sectorDefault);
  return order.slice(from).map((key, index) => ({
    value: key,
    label: index === 0 ? `${key} — sector default` : `${key} — lowered`,
  }));
}

/**
 * Findings of one severity, by field.
 *
 * Kept apart rather than merged, because the two are shown on different terms: an advisory
 * says something the certification body does not already know and is on screen from the
 * start, while a blocking finding on an empty intake only restates that the intake is
 * empty and waits until the body asks to run the determination.
 */
function index(
  validation: IntakeValidation | null,
  severity: "blocking" | "advisory",
): Record<string, string> {
  const found: Record<string, string> = {};
  for (const card of validation?.cards ?? []) {
    for (const issue of card[severity] as IntakeIssue[]) {
      found[issue.field] ??= issue.reference
        ? `${issue.detail} (${issue.reference})`
        : issue.detail;
    }
  }
  return found;
}

/**
 * Screen 24's select labels each auditor with what the register knows about them.
 *
 * Nothing here is disabled. The prototype greys out "not yet verified", and greying out in
 * a dropdown is not a refusal — the id can still be posted. The refusal is the server's
 * (`not_verified` blocks), and this label is why a body would not pick them in the first
 * place.
 */
function auditorLabel(auditor: AuditorSummary): string {
  // A null status is an auditor nobody invited — the same fact sign-in reads, seen from
  // the register. Named rather than left blank, because a blank reads as "fine".
  const status = auditor.status ?? "not invited";
  return status === "active" ? auditor.name : `${auditor.name} — ${status.replace(/_/g, " ")}`;
}

function toggle(values: readonly number[], value: number): number[] {
  return values.includes(value)
    ? values.filter((entry) => entry !== value)
    : [...values, value];
}

function message(cause: unknown, subject: string): string {
  return cause instanceof ApiError ? cause.message : `${subject} could not be read.`;
}

export type { AllocationGate };
